import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCustodianAuthorityReview,
  getCustodianPendingAdmission,
  submitCustodianAuthorityDecision,
} from '../../api/custodianApi';
import { CustodianConfirmModal } from '../../components/custodian/CustodianConfirmModal';
import { Button } from '../../components/ui/Button';
import { DataTable } from '../../components/ui/DataTable';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { FilterSelect } from '../../components/ui/FilterSelect';
import { NoticeBanner } from '../../components/ui/NoticeBanner';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import {
  canRecordAuthorityDecision,
  custodianPackageDetailPath,
  displayValue,
} from './custodianReviewLabels';

const INTAKE_OPTIONS = [
  'All intake states',
  'Awaiting review',
  'Needs clarification',
  'Accepted for admission',
  'Rejected back to provider',
];

const DECISION_OPTIONS = ['All decisions', 'Pending', 'Needs clarification'];

const BASE_FACILITY_OPTIONS = ['All facilities', 'UMB Line A', 'SPV 2026-1'];

function intakeLabel(row) {
  const authority = row?.authority_state;
  if (authority === 'PENDING') return 'Awaiting review';
  return displayValue(authority);
}

function decisionLabel(row) {
  const authority = row?.authority_state;
  if (authority === 'PENDING') return 'Pending';
  return displayValue(authority);
}

function evidenceLabel(row) {
  if (typeof row?.file_count === 'number') {
    return `${row.file_count} file${row.file_count === 1 ? '' : 's'}`;
  }
  return displayValue(null);
}

function rowMatchesFilters(row, intakeFilter, decisionFilter, facilityFilter) {
  if (intakeFilter !== 'All intake states' && intakeLabel(row) !== intakeFilter) {
    return false;
  }
  if (decisionFilter !== 'All decisions' && decisionLabel(row) !== decisionFilter) {
    return false;
  }
  if (facilityFilter !== 'All facilities' && row?.program !== facilityFilter) {
    return false;
  }
  return true;
}

export function AwaitingReviewPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingError, setPendingError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [intakeFilter, setIntakeFilter] = useState('All intake states');
  const [decisionFilter, setDecisionFilter] = useState('All decisions');
  const [facilityFilter, setFacilityFilter] = useState('All facilities');
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadQueue() {
      setLoading(true);
      setError('');
      setPendingError('');
      const [reviewResult, pendingResult] = await Promise.allSettled([
        getCustodianAuthorityReview({ page, pageSize }),
        getCustodianPendingAdmission({ page: 1, pageSize: 50 }),
      ]);
      if (cancelled) return;
      if (reviewResult.status === 'fulfilled') {
        setItems(reviewResult.value?.items || []);
        setTotal(reviewResult.value?.meta?.total || 0);
      } else {
        setItems([]);
        setTotal(0);
        setError(
          reviewResult.reason?.message ||
            'Unable to load packages awaiting Custodian authority review.'
        );
      }
      if (pendingResult.status === 'fulfilled') {
        setPendingItems(pendingResult.value?.items || []);
      } else {
        setPendingItems([]);
        setPendingError(
          pendingResult.reason?.message ||
            'Unable to load packages approved for vault admission.'
        );
      }
      if (!cancelled) setLoading(false);
    }

    loadQueue();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, reloadToken]);

  const refresh = () => setReloadToken((value) => value + 1);

  const facilityOptions = useMemo(() => {
    const extras = Array.from(
      new Set(items.map((row) => row.program).filter(Boolean))
    ).filter((name) => !BASE_FACILITY_OPTIONS.includes(name));
    return [...BASE_FACILITY_OPTIONS, ...extras];
  }, [items]);

  const visibleRows = useMemo(
    () =>
      items.filter((row) =>
        rowMatchesFilters(row, intakeFilter, decisionFilter, facilityFilter)
      ),
    [items, intakeFilter, decisionFilter, facilityFilter]
  );

  const handleConfirm = async () => {
    if (!confirm?.packageId || !confirm?.decision || busy) return;
    setBusy(true);
    setActionError('');
    setNotice('');
    try {
      await submitCustodianAuthorityDecision(confirm.packageId, confirm.decision);
      setConfirm(null);
      setNotice(
        confirm.decision === 'APPROVED'
          ? 'Custodian authority recorded as APPROVED. Technical vault admission is still required.'
          : 'Custodian authority recorded as REJECTED. This package will not be admitted to the Authoritative vault.'
      );
      refresh();
      navigate(custodianPackageDetailPath(confirm.packageId));
    } catch (requestError) {
      setActionError(
        requestError.message || 'Unable to record the Custodian authority decision.'
      );
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: 'review_ref',
      header: 'Review ref',
      sortValue: (row) => row.reference_number || row.review_task_id,
      render: (row) => (
        <Link
          to={custodianPackageDetailPath(row.package_id)}
          className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan hover:underline"
        >
          {displayValue(row.reference_number || row.review_task_id)}
        </Link>
      ),
    },
    {
      key: 'asset_id',
      header: 'Asset',
      render: (row) => (
        <Link
          to={custodianPackageDetailPath(row.package_id)}
          className="font-mono text-[11px] text-eorig-navy-600 hover:text-eorig-cyan hover:underline"
        >
          {displayValue(row.asset_id)}
        </Link>
      ),
    },
    {
      key: 'package_id',
      header: 'Package',
      render: (row) => (
        <Link
          to={custodianPackageDetailPath(row.package_id)}
          className="font-mono text-[11px] text-eorig-navy-600 hover:text-eorig-cyan hover:underline"
        >
          {displayValue(row.package_id)}
        </Link>
      ),
    },
    {
      key: 'program',
      header: 'Facility / SPV',
      render: (row) => displayValue(row.program),
    },
    {
      key: 'evidence',
      header: 'Evidence completeness',
      render: (row) => evidenceLabel(row),
    },
    {
      key: 'recommendation',
      header: 'Provider recommendation',
      render: (row) =>
        row.recommendation ? (
          <StatusPill value={row.recommendation} />
        ) : (
          displayValue(row.recommendation)
        ),
    },
    {
      key: 'intake',
      header: 'Intake state',
      render: (row) => intakeLabel(row),
    },
    {
      key: 'decision',
      header: 'Decision',
      render: (row) =>
        row.authority_state ? (
          <StatusPill
            value={row.authority_state}
            label={decisionLabel(row)}
          />
        ) : (
          decisionLabel(row)
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (row) => {
        const pending = canRecordAuthorityDecision(row);
        const reviewRef = displayValue(row.reference_number || row.review_task_id);
        return (
          <div
            className="flex flex-wrap gap-1"
            role="group"
            aria-label={`Decisions for ${reviewRef}`}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Button
              type="button"
              variant="navy"
              className="px-2.5 py-1 text-[10px]"
              disabled={!pending || busy}
              onClick={() =>
                setConfirm({
                  packageId: row.package_id,
                  decision: 'APPROVED',
                })
              }
            >
              Accept
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="px-2.5 py-1 text-[10px] text-red-600"
              disabled={!pending || busy}
              onClick={() =>
                setConfirm({
                  packageId: row.package_id,
                  decision: 'REJECTED',
                })
              }
            >
              Reject
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="px-2.5 py-1 text-[10px]"
              disabled
              title="Clarification is not a Custodian authority outcome in this workflow."
            >
              Clarify
            </Button>
          </div>
        );
      },
    },
  ];

  const emptyMessage =
    items.length === 0
      ? 'No packages are currently awaiting Custodian authority review.'
      : 'No packages match these filters.';

  return (
    <div className="space-y-5 text-eorig-ink">
      <section className="space-y-3 rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5">
        <p className="text-sm leading-relaxed text-eorig-muted">
          Provider-to-custodian handoff: evidence completeness, provider
          recommendation, and the admission decision — accept, reject to
          provider, or request clarification. Accept moves the package to
          Approved — awaiting vault admission. It leaves that list only after
          Admit.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            label="Intake"
            ariaLabel="Filter by intake state"
            options={INTAKE_OPTIONS.map((option) => ({ value: option, label: option }))}
            value={intakeFilter}
            onChange={setIntakeFilter}
          />
          <FilterSelect
            label="Decision"
            ariaLabel="Filter by decision"
            options={DECISION_OPTIONS.map((option) => ({ value: option, label: option }))}
            value={decisionFilter}
            onChange={setDecisionFilter}
          />
          <FilterSelect
            label="Facility"
            ariaLabel="Filter by facility"
            options={facilityOptions.map((option) => ({ value: option, label: option }))}
            value={facilityFilter}
            onChange={setFacilityFilter}
          />
        </div>
      </section>

      {error ? <ErrorBanner message={error} onRetry={refresh} /> : null}
      {pendingError ? <ErrorBanner message={pendingError} onRetry={refresh} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}
      {notice ? <NoticeBanner>{notice}</NoticeBanner> : null}

      <DataTable
        columns={columns}
        rows={visibleRows}
        rowKey={(row) => row.package_id}
        loading={loading}
        emptyMessage={emptyMessage}
        minWidth={960}
      />
      {total > pageSize ? (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          disabled={loading}
        />
      ) : null}

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-eorig-ink">
            Approved — awaiting vault admission
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-eorig-muted">
            These packages already have Custodian authority APPROVED. Open one
            to admit it into the vault. This list is not affected by the
            filters above.
          </p>
        </div>
        <DataTable
          columns={[
            columns[0],
            columns[1],
            columns[3],
            columns[5],
            {
              key: 'authority_state',
              header: 'Authority',
              render: (row) =>
                row.authority_state ? (
                  <StatusPill value={row.authority_state} />
                ) : (
                  displayValue(row.authority_state)
                ),
            },
            {
              key: 'open',
              header: 'Action',
              sortable: false,
              render: (row) => (
                <Link
                  to={custodianPackageDetailPath(row.package_id)}
                  className="text-xs font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
                >
                  Open
                </Link>
              ),
            },
          ]}
          rows={pendingItems}
          rowKey={(row) => row.package_id}
          loading={loading}
          emptyMessage="No packages are approved and waiting for vault admission."
          minWidth={880}
        />
      </section>

      <CustodianConfirmModal
        open={Boolean(confirm)}
        title={
          confirm?.decision === 'REJECTED'
            ? 'Reject authority'
            : 'Approve authority'
        }
        description={
          confirm?.decision === 'REJECTED'
            ? 'This records the Custodian authority decision. The package will not be admitted to the Authoritative vault.'
            : 'This records the Custodian authority decision. The package will still require technical vault admission.'
        }
        confirmLabel={confirm?.decision === 'REJECTED' ? 'Reject' : 'Approve'}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
}
